import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "components/Common/custom-select";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { generateClientCode, changeClientStatus, fetchClient, createClientRequest,fetchAccountList } from "store/actions";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";
import useButtonAccess from "components/Common/useButtonAccess";

const TimeSheetClient = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);

  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);

  const {
    client,
    clientCode,
    loading,
    accountList,
    success,
    error,
  } = useSelector((state) => ({
    client: state.TimesheetSetupClientReducer?.client ?? [],
    clientCode: state.TimesheetSetupClientReducer?.clientCode ?? '',
    loading: state.TimesheetSetupClientReducer?.loading,
    accountList: state.TimesheetSetupClientReducer?.accountList ?? [],
    error: state.TimesheetSetupClientReducer?.error,
    success: state.TimesheetSetupClientReducer?.success,
  }));


  const serviceOptions = (clientCode?.client_service ?? []).map(ded => ({
    value: ded.service_id,
    label: ded.service_code
  }));

  const accountOptions = (accountList ?? []).map(acc => ({
    value: acc.account_id,
    label: acc.account_name,
  }));


  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchClient(unitId));
    dispatch(generateClientCode(unitId));
    dispatch(fetchAccountList(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchClient(authDetails?.unit_id));
      dispatch(generateClientCode(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
      setClientServiceData(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [clientServiceData, setClientServiceData] = useState(null);

  useEffect(() => {
    const accessRight = getAccessRights(location.pathname, history);
  }, [location]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: clientCode?.client_code && clientCode?.client_code[0] && clientCode?.client_code[0].client_code ? clientCode?.client_code[0].client_code : '',
      clientName: "",
      shortName: "",
      address: "",
      phone: "",
      contactPerson: "",
      phonePrefix: "+91",
      email: "",
      services: [],
      accounts: null,
    },
    validationSchema: Yup.object({
      code: Yup.string().required("Client code is required"),
      clientName: Yup.string().required("Client Name is required"),
      shortName: Yup.string().required("Short Name is required"),
      address: Yup.string().required("Address is required"),
      phone: Yup.string()
        .when('phonePrefix', {
          is: '+91',
          then: Yup.string()
            .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
            .required("Phone number is required"),
          otherwise: Yup.string()
            .matches(/^[0-9]{1,10}$/, "Phone number must be up to 10 digits")
            .required("Phone number is required"),
        }),
      contactPerson: Yup.string().required("Contact Person is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      services: Yup.array().min(1, "Service is required"),
    }),

    onSubmit: (values, { resetForm }) => {
      const client_service_ids = values.services.map(option => option.value);
      const formData = new FormData();
      formData.append("client_id", editId ? editId : 0);
      formData.append("client_code", values.code);
      formData.append("client_name", values.clientName);
      formData.append("client_short_name", values.shortName);
      formData.append("client_address", values.address);
      formData.append("client_phone", values.phone);
      formData.append("client_contact_person", values.contactPerson);
      formData.append("client_email", values.email);
      formData.append("client_service_id", client_service_ids);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("country_code", values.phonePrefix);
      formData.append("account_id", values.accounts.value);
      dispatch(createClientRequest(formData));
      resetForm();
      setEditId(null);
      setClientServiceData(null);
    },
  });

  const handleEdit = (row) => {
    const selectedIds = row.client_service_id.split(',');
    const cleanedIds = selectedIds.map(id => parseInt(id.trim(), 10));
    const selectedValues = serviceOptions.filter(option => cleanedIds.includes(option.value));
    const selectedAccount = accountOptions.find(opt => opt.label === row.account_name);
    formik.setValues({
      code: row.client_code,
      clientName: row.client_name,
      shortName: row.short_name,
      address: row.client_address,
      phone: row.phone_no,
      contactPerson: row.contact,
      email: row.email,
      services: selectedValues,
      accounts: selectedAccount || null,
      phonePrefix: row.country_code || "+91",
    });
    setEditId(row.client_id);
    setClientServiceData(selectedValues);
  };

  const handleToggleStatus = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this client.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("client_id", id);
        formData.append("create_by", authDetails?.user_id);
        formData.append("unit_id", authDetails?.unit_id);
        dispatch(changeClientStatus(formData));
      }
    });
  };

  const CustomSelect = ({
  closeMenuOnScroll = true,
  menuPortalTarget = document.body,
  menuPosition = "fixed",
  menuShouldScrollIntoView = false,
  ...props
}) => {
  return (
    <Select
      {...props}
      closeMenuOnScroll={closeMenuOnScroll}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      menuShouldScrollIntoView={menuShouldScrollIntoView}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
};
 

  // const columns = [
  //   { Header: "S.No", Cell: ({ row }) => client.length - row.index, className: "serial-no text-center", applyHeaderClass: true, },
  //   { Header: "Code", accessor: "client_code" },
  //   { Header: "Name", accessor: "client_name" },
  //   { Header: "S-Name", accessor: "short_name" },
  //   { Header: "Services", accessor: "client_services" },
  //   { Header: "Contact", accessor: "contact" },
  //   { Header: "Email", accessor: "email" },
  //   //{ Header: "Ph-No", accessor: "phone_no" },
  //   //{ Header: "Address", accessor: "client_address" },
  //   { Header: "Created By", accessor: "create_by" },
  //   { Header: "Created On", accessor: "create_date" },
  //   {
  //     Header: "Actions", headerClassName: 'text-center',
  //     className: 'text-center',
  //     Cell: (cellProps) => {
  //       const { b_active, client_id } = cellProps.row.original;
  //       const isActive = b_active === 1; // Status 1 = Active
  //       return (
  //         <div className="text-center">
  //           <Link to="#" style={{ color: isActive ? "green" : "red" }} onClick={() => handleToggleStatus(client_id, isActive)} className="me-2">
  //             <i className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`} id={`status-tooltip-${cellProps.row.id}`} />
  //             <UncontrolledTooltip placement="top" target={`status-tooltip-${cellProps.row.id}`}>
  //               {isActive ? "Mark Inactive" : "Mark Active"}
  //             </UncontrolledTooltip>
  //           </Link>
  //           <Link to="#" className="text-success ml-3" onClick={() => handleEdit(cellProps.row.original)}>
  //             <i className="bx bxs-edit font-size-18" id="edittooltip" />
  //             <UncontrolledTooltip placement="top" target="edittooltip">
  //               Edit
  //             </UncontrolledTooltip>
  //           </Link>
  //         </div>
  //       );
  //     },
  //   },
  // ];

  const columns = [
    {
    Header: "Actions",
    headerClassName: "text-center",
    className: "text-center",
    Cell: (cellProps) => {
      const { b_active, client_id } = cellProps.row.original;
      const isActive = b_active === 1;

      return (
        <div className="text-center">

          {/* STATUS TOGGLE */}
          {canDelete && (
            <>
              <span
                className="me-2"
                style={{ color: isActive ? "green" : "red", cursor: "pointer" }}
                id={`status-${client_id}`}
                onClick={() => handleToggleStatus(client_id, isActive)}
              >
                <i className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`} />
              </span>

              <UncontrolledTooltip
                placement="top"
                target={`status-${client_id}`}
                delay={{ show: 300, hide: 100 }}
              >
                {isActive ? "Mark Inactive" : "Mark Active"}
              </UncontrolledTooltip>
            </>
          )}

          {/* EDIT */}
          {canEdit && (
            <>
              <span
                className="text-success ms-2"
                style={{ cursor: "pointer" }}
                id={`edit-${client_id}`}
                onClick={() => handleEdit(cellProps.row.original)}
              >
                <i className="bx bxs-edit font-size-18" />
              </span>

              <UncontrolledTooltip
                placement="top"
                target={`edit-${client_id}`}
                delay={{ show: 300, hide: 100 }}
              >
                Edit
              </UncontrolledTooltip>
            </>
          )}

        </div>
      );
    },
  },
  { Header: "S.No", Cell: ({ row }) => client.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
  { Header: "Code", accessor: "client_code" },
  { Header: "Name", accessor: "client_name" },
  { Header: "S-Name", accessor: "short_name" },
  { Header: "Services", accessor: "client_services" },
  { Header: "Contact", accessor: "contact" },
  { Header: "Email", accessor: "email" },
  { Header: "Account", accessor: "account_name" },
  { Header: "Created By", accessor: "create_by" },
  { Header: "Created On", accessor: "create_date" },

  
];

  const handleCancel = () => {
    setEditId(null); // Reset back to Save mode
    formik.resetForm();
    setClientServiceData(null); // Clear Service select
    setIsEdit(false);
  };

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Timesheet" breadcrumbItem="Client Master" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row>
                <Col lg={4}>
                  <FormGroup>
                    <Label>Client Code</Label>
                    <Input type="text" name="code" disabled value={formik.values.code} onChange={formik.handleChange}
                      onBlur={formik.handleBlur} />
                    {formik.touched.code && formik.errors.code && (
                      <div className="text-danger">
                        {formik.errors.code}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>Client Name <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="clientName"
                      maxLength={50}
                      onChange={formik.handleChange}
                      value={formik.values.clientName}
                      invalid={
                        formik.touched.clientName && formik.errors.clientName
                      }
                    />
                    {formik.touched.clientName && formik.errors.clientName && (
                      <div className="text-danger">
                        {formik.errors.clientName}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>
                      Short Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="shortName"
                      maxLength={20}
                      onChange={formik.handleChange}
                      value={formik.values.shortName}
                      invalid={
                        formik.touched.shortName && formik.errors.shortName
                      }
                    />
                    {formik.touched.shortName && formik.errors.shortName && (
                      <div className="text-danger">
                        {formik.errors.shortName}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>Address <span className="text-danger">*</span></Label>
                    <Input
                      type="textarea"
                      name="address"
                      maxLength={100}
                      onChange={formik.handleChange}
                      value={formik.values.address}
                      style={{ maxHeight: "33.1px", minHeight: "33.1px" }}
                    />
                    {formik.touched.address && formik.errors.address && (
                      <div className="text-danger">{formik.errors.address}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>Contact Person <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="contactPerson"
                      maxLength={50}
                      onChange={formik.handleChange}
                      value={formik.values.contactPerson}
                    />
                    {formik.touched.contactPerson &&
                      formik.errors.contactPerson && (
                        <div className="text-danger">
                          {formik.errors.contactPerson}
                        </div>
                      )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>Email <span className="text-danger">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      maxLength={100}
                      onChange={formik.handleChange}
                      value={formik.values.email}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="text-danger">{formik.errors.email}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>Phone No <span className="text-danger">*</span></Label>
 
                    <div className="d-flex gap-2">
                      {/* PREFIX */}
                      <Input
                        type="text"
                        name="phonePrefix"
                        style={{ maxWidth: "100px" }}
                        value={formik.values.phonePrefix}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        maxLength={10}
                        placeholder="+91"
                      />
 
                      {/* PHONE NUMBER */}
                      <Input
                        type="text"
                        name="phone"
                        value={formik.values.phone}
                        onChange={(e) => {
                          // const value = e.target.value.replace(/\D/g, "");
                          // formik.setFieldValue("phone", value);
                          const value = e.target.value.replace(/\D/g, "");
                          // For India, restrict to 10 digits; for others, allow up to 10 digits
                          if (formik.values.phonePrefix === '+91') {
                            formik.setFieldValue("phone", value.slice(0, 10));
                          } else {
                            formik.setFieldValue("phone", value.slice(0, 10));
                          }
                        }}
                        maxLength={formik.values.phonePrefix === '+91' ? 10 : 10}
                        // }}
                        // maxLength={10}
                        placeholder="Enter phone number"
                      />
                    </div>
 
                    {(formik.touched.phonePrefix && formik.errors.phonePrefix) && (
                      <div className="text-danger">{formik.errors.phonePrefix}</div>
                    )}
 
                    {(formik.touched.phone && formik.errors.phone) && (
                      <div className="text-danger">{formik.errors.phone}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={4}>
                  <FormGroup>
                    <Label>
                      Service <span className="text-danger">*</span>
                    </Label>
                    <CustomSelect
                      id="services"
                      name="services"
                      isMulti={true}
                      options={serviceOptions}
                      value={clientServiceData}
                      closeMenuOnScroll={false}
                      menuPosition="absolute"
                      menuPortalTarget={null}
                      onChange={(selectedOption) => { formik.setFieldValue("services", selectedOption); setClientServiceData(selectedOption); }}
                      onBlur={() => formik.setFieldTouched("services", true)}
                    />
                    {formik.touched.services && formik.errors.services && (
                      <div className="text-danger">
                        {formik.errors.services}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col lg={4}>
                  <FormGroup>
                    <Label>
                      Account <span className="text-danger">*</span>
                    </Label>
                    <CustomSelect
                      id="accounts"
                      name="accounts"
                      isMulti={false}
                      options={accountOptions}
                         closeMenuOnScroll={false}
                      menuPosition="absolute"
                      menuPortalTarget={null}
                      value={formik.values.accounts}
                      onChange={(selectedOption) =>
                        formik.setFieldValue("accounts", selectedOption)
                      }
                      onBlur={() => formik.setFieldTouched("accounts", true)}
                    />
 
                    {formik.touched.accounts && formik.errors.accounts && (
                      <div className="text-danger">{formik.errors.accounts}</div>
                    )}
 
                  </FormGroup>
                </Col>
 

                {/* <Col md="4" className="text-end mt-4">
                  <Button type="submit" color="primary" className="btn button-save me-2">
                    <i className="fas fa-save me-1" />{" "} {editId ? "Update" : "Save"} </Button>
                  <Button type="button" className="btn button-cancel" onClick={() => { formik.resetForm(); setEditId(null); }}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col> */}
                <Col className="d-flex mob-center align-items-center justify-content-end mt-2">                
                  {canCreate && (
                    <Button type="submit" color="primary" className="btn button-save me-2">
                      <i className="fas fa-save me-1" /> {editId ? "Update" : "Save"}
                    </Button>
                  )}
                  <Button type="button" className="btn button-cancel me-2" onClick={handleCancel}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="timesheet-table">
              <TableContainer
                columns={columns}
                data={client}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TimeSheetClient;
